import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AppContext } from "./context/AppContext";

const WORDS = [
  "apple","arrow","atlas","amber","angel","brick","blade","bloom","brave","blaze",
  "cedar","chief","coral","crane","crisp","delta","derby","dingo","drift","dune",
  "eagle","ember","epoch","ergo","essay","fable","falcon","fancy","feast","flame",
  "giant","glacier","globe","grace","grape","haven","hawk","honey","honor","hound",
  "ivory","image","inlet","iris","iron","jade","jaguar","jasper","jewel","jungle",
  "karma","kelp","kite","knight","knoll","lemon","lever","light","linen","lodge",
  "maple","marble","marsh","merit","mirth","noble","north","novel","nymph","ocean",
  "onyx","orbit","otter","ouzel","ozone","pearl","petal","pilot","pine","pivot",
  "quartz","quest","quill","quota","queen","raven","realm","relay","ridge","river",
  "sable","sage","salt","scout","shade","sigma","silver","slate","solar","spark",
  "storm","swift","table","talon","thorn","tiger","titan","torch","tower","trail",
  "ultra","umbra","unity","upper","urban","valor","vault","velvet","vista","vivid",
  "walnut","wave","wheat","whirl","willow","xenon","yacht","yarrow","zebra","zenith",
  "acorn","agate","aloft","amino","anvil","basil","bison","blast","bluff","boreal",
  "brisk","brook","cactus","canto","cape","chalk","charm","chase","chord","cleft",
  "cliff","clover","cobalt","comet","copse","crest","crown","crush","cubic","curve",
  "dusk","dusk","epoch","fern","finch","fjord","fleet","flint","floe","flora",
];

function generatePassphrase() {
  const picked = [];
  const pool = [...WORDS];
  for (let i = 0; i < 4; i++) {
    const idx = Math.floor(Math.random() * pool.length);
    picked.push(pool.splice(idx, 1)[0]);
  }
  return picked.join("-");
}

const API_BASE = process.env.REACT_APP_API_BASE;

export default function PassphraseScreen() {
  const navigate = useNavigate();
  const { setPlayer, setUserId, setCredits } = useContext(AppContext);

  const [mode, setMode] = useState("new"); // "new" | "returning"
  const [passphrase, setPassphrase] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [inputPassphrase, setInputPassphrase] = useState("");
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState("idle"); // "idle" | "loading" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    setPassphrase(generatePassphrase());
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(passphrase);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleRefresh = () => {
    setPassphrase(generatePassphrase());
    setCopied(false);
  };

  const handleCreate = async () => {
    if (!displayName.trim()) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await axios.post(`${API_BASE}/api/auth/create`, {
        passphrase,
        displayName: displayName.trim(),
      });
      const { userId, credits } = res.data;
      setUserId(userId);
      setCredits(credits ?? 0);
      setPlayer(displayName.trim());
      navigate("/");
    } catch (err) {
      const msg = err.response?.data?.error || "Something went wrong";
      setErrorMsg(msg);
      setStatus("error");
      if (err.response?.status === 409) setPassphrase(generatePassphrase());
    }
  };

  const handleLogin = async () => {
    if (!inputPassphrase.trim()) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await axios.post(`${API_BASE}/api/auth/login`, {
        passphrase: inputPassphrase.trim(),
      });
      const { userId, displayName, credits } = res.data;
      setUserId(userId);
      setCredits(credits ?? 0);
      setPlayer(displayName);
      navigate("/");
    } catch (err) {
      const msg = err.response?.data?.error || "Something went wrong";
      setErrorMsg(msg);
      setStatus("error");
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <header style={styles.header}>
          <h2 style={styles.title}>AI Arcade</h2>
          <div style={styles.tabs}>
            <button
              style={mode === "new" ? styles.tabActive : styles.tab}
              onClick={() => { setMode("new"); setErrorMsg(""); }}
            >
              New Account
            </button>
            <button
              style={mode === "returning" ? styles.tabActive : styles.tab}
              onClick={() => { setMode("returning"); setErrorMsg(""); }}
            >
              Returning
            </button>
          </div>
        </header>

        <main style={styles.main}>
          {mode === "new" && (
            <>
              <p style={styles.desc}>
                Your passphrase <em>is</em> your account. Save it somewhere safe —
                it's the only way to get back in.
              </p>

              <div style={styles.phraseBox}>
                <span style={styles.phraseText}>{passphrase}</span>
              </div>

              <div style={styles.btnRow}>
                <button style={styles.copyBtn} onClick={handleCopy}>
                  {copied ? "Copied!" : "Copy"}
                </button>
                <button style={styles.refreshBtn} onClick={handleRefresh}>
                  New phrase
                </button>
              </div>

              <input
                style={styles.input}
                placeholder="Choose a display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={20}
              />

              <button
                style={{
                  ...styles.primaryBtn,
                  opacity: status === "loading" || !displayName.trim() ? 0.6 : 1,
                }}
                onClick={handleCreate}
                disabled={status === "loading" || !displayName.trim()}
              >
                {status === "loading" ? "Creating…" : "I've saved it — Create Account"}
              </button>
            </>
          )}

          {mode === "returning" && (
            <>
              <p style={styles.desc}>Enter your passphrase to pick up where you left off.</p>

              <input
                style={styles.input}
                placeholder="word-word-word-word"
                value={inputPassphrase}
                onChange={(e) => setInputPassphrase(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                autoFocus
              />

              <button
                style={{
                  ...styles.primaryBtn,
                  marginTop: 12,
                  opacity: status === "loading" ? 0.6 : 1,
                }}
                onClick={handleLogin}
                disabled={status === "loading" || !inputPassphrase.trim()}
              >
                {status === "loading" ? "Logging in…" : "Log In"}
              </button>
            </>
          )}

          {errorMsg && <p style={styles.error}>{errorMsg}</p>}
        </main>

        <footer style={styles.footer}>
          <button style={styles.ghostBtn} onClick={() => navigate("/")}>
            Back to Home
          </button>
        </footer>
      </div>
    </div>
  );
}

const styles = {
  page: {
    minHeight: "100vh",
    background: "url(/Log_In_Screen_Background.jpg) center / cover no-repeat",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: 16,
  },
  card: {
    width: "100%",
    maxWidth: 400,
    background: "#ffffffee",
    borderRadius: 20,
    overflow: "hidden",
    boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    padding: "20px 20px 0",
    textAlign: "center",
  },
  title: {
    margin: "0 0 16px",
    fontSize: 22,
    fontWeight: 700,
  },
  tabs: {
    display: "flex",
    borderBottom: "2px solid #e5e7eb",
  },
  tab: {
    flex: 1,
    padding: "10px 0",
    border: "none",
    background: "none",
    fontSize: 14,
    color: "#6b7280",
    cursor: "pointer",
  },
  tabActive: {
    flex: 1,
    padding: "10px 0",
    border: "none",
    borderBottom: "2px solid #007bff",
    background: "none",
    fontSize: 14,
    fontWeight: 700,
    color: "#007bff",
    cursor: "pointer",
    marginBottom: -2,
  },
  main: {
    padding: "20px 24px",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  desc: {
    margin: 0,
    fontSize: 13,
    color: "#4b5563",
    lineHeight: 1.5,
  },
  phraseBox: {
    background: "#f0f9ff",
    border: "2px dashed #93c5fd",
    borderRadius: 12,
    padding: "16px 20px",
    textAlign: "center",
  },
  phraseText: {
    fontSize: 18,
    fontWeight: 700,
    fontFamily: "monospace",
    letterSpacing: "0.05em",
    color: "#1e40af",
    wordBreak: "break-all",
  },
  btnRow: {
    display: "flex",
    gap: 8,
  },
  copyBtn: {
    flex: 1,
    padding: "8px 0",
    borderRadius: 20,
    border: "1px solid #007bff",
    background: "#fff",
    color: "#007bff",
    fontSize: 14,
    cursor: "pointer",
  },
  refreshBtn: {
    flex: 1,
    padding: "8px 0",
    borderRadius: 20,
    border: "1px solid #9ca3af",
    background: "#fff",
    color: "#6b7280",
    fontSize: 14,
    cursor: "pointer",
  },
  primaryBtn: {
    width: "100%",
    padding: "12px 0",
    borderRadius: 20,
    border: "none",
    background: "#007bff",
    color: "#fff",
    fontSize: 15,
    fontWeight: 600,
    cursor: "pointer",
  },
  input: {
    width: "100%",
    padding: "10px 14px",
    borderRadius: 20,
    border: "1px solid #d1d5db",
    fontSize: 15,
    fontFamily: "monospace",
    boxSizing: "border-box",
  },
  error: {
    margin: 0,
    fontSize: 13,
    color: "#dc2626",
    textAlign: "center",
  },
  footer: {
    padding: "12px 24px 20px",
    borderTop: "1px solid #f3f4f6",
    textAlign: "center",
  },
  ghostBtn: {
    background: "none",
    border: "none",
    color: "#9ca3af",
    fontSize: 13,
    cursor: "pointer",
  },
};
